// src/features/financial_indicators/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { FormActionsNew } from "@/features/FormCard/Actions";
import { InputField, SelectField } from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

// import country options
import { countries_options } from "@/components/Form/CountryPhone/data";
// import enum options and utilities from filter
import {
  frequencyEditOptions,
  preprocessEnumToNumber,
  sourceEditOptions,
} from "./filter";

const client = "instit-quantilia";
const table_name = "financial_indicators";

const FormSchema = z.object({
  code: z.string().min(1, "Code is required"),
  // source is optional number
  source: z.preprocess(
    preprocessEnumToNumber,
    z.number().optional().nullable(),
  ),
  short_name: z.string().min(1, "Short Name is required"),
  name: z.string().min(1, "Name is required"),
  country: z.string().min(1, "Country is required"),
  // frequency is optional number
  frequency: z.preprocess(
    preprocessEnumToNumber,
    z.number().optional().nullable(),
  ),
});

export function FinancialIndicatorsNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => navigate(-1);

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New Financial Indicator
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Code & Source */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="code"
                    label={t(`${table_name}.columns.code`, {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <SelectField
                    name="source"
                    label={t(`${table_name}.columns.source`, {
                      ns: "tables",
                      defaultValue: "Source",
                    })}
                    options={sourceEditOptions}
                    control={form.control}
                  />
                </div>
                {/* Row 2: Short Name & Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="short_name"
                    label={t(`${table_name}.columns.short_name`, {
                      ns: "tables",
                      defaultValue: "Short Name",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Name",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
                {/* Row 3: Country & Frequency */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="country"
                    label={t(`${table_name}.columns.country`, {
                      ns: "tables",
                      defaultValue: "Country",
                    })}
                    options={countries_options}
                    control={form.control}
                  />
                  <SelectField
                    name="frequency"
                    label={t(`${table_name}.columns.frequency`, {
                      ns: "tables",
                      defaultValue: "Frequency",
                    })}
                    options={frequencyEditOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields ? NewDiff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel (navigate back) confirmation */}
          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
